import { LatLng, LatLngBounds } from 'leaflet'

export const CENTER_OF_GERMANY_POSITION = {
  latitude: 51.163375,
  longitude: 10.447683333333
}

export const MAP_TOKEN =
  'pk.eyJ1IjoibG9rYWxwb3J0YWwiLCJhIjoiY2ltNjRwYTdtMDAzeHh3a204NTZyNHJhbCJ9.8701BepMtE87iD0GxNv3uA'

export const MAP_PATH_OPTIONS = {
  color: '#989898', // stroke (border) color, $color-gray-tint-2
  fillColor: '#737373', // $color-gray-tint-1
  fillOpacity: 0.4,
  interactive: false, // stroke opacity
  opacity: 1, // stroke width
  weight: 1
}

// bounding box which covers Germany
export const MAP_OPTION_MAX_BOUNDS = new LatLngBounds(new LatLng(47.27021, 5.86624), new LatLng(55.05814, 15.04205))
export const MAP_OPTION_ATTRIBUTION_CONTROL = false
export const MAP_ZOOM_STEP_SIZE = 1
export const MAP_OPTION_ZOOM_CONTROL = false
export const MAP_OPTION_TILE_SIZE = 512
export const MAP_OPTION_ZOOM_OFFSET = -1

export const MAP_ATTRIBUTION =
  '<a href="https://www.openstreetmap.org/about/" rel="noopener" target="_blank">© OpenStreetMap</a>'

// minimum distance (between old and new southwest or northeast map bounding box corners)
// when dragging or zooming the map to show the "search this area" button
export const MAP_DISTANCE_THRESHOLD = 1000 // m

// the default Mapbox style which will be used if no Lopo Admin toggle
// with the name `mapStyle` is found or active
export const MAP_DEFAULT_MAPBOX_STYLE = 'lokalportal/clewx9goj004a01phpzwfhgbz'

// the zoom levels the map falls back to when the instance configures none: neither in the
// settings of `/api/features` nor in a Lopo Admin toggle named `mapZoom`
export const MAP_OPTION_MAX_ZOOM = 16
export const MAP_OPTION_MIN_ZOOM = 12

// How far the tile layer is allowed to go, independent of what an instance configures.
// `L.TileLayer` defaults to 18, and above it the map draws no tiles at all —
// an instance configuring 19 would get an empty map. The tile server answers well past that
// (measured: real images up to tile zoom 22, which is map zoom 23), and the backend accepts at
// most 21, so this covers every range an instance can ask for, with real tiles rather than
// stretched ones. It cannot follow the configured range: react-leaflet updates only `opacity`
// and `zIndex` on a grid layer that already exists.
export const MAP_OPTION_TILE_MAX_ZOOM = 21

// How far out a project map may go, counted from the zoom its bounding box fits at. Relative
// rather than absolute, because the zoom a project fits at depends on the project: a single
// building fits many levels deeper than a motorway section, and one fixed level would either
// stop a small project short or keep a long one from fitting at all.
export const MAP_PROJECT_ZOOM_FLOOR_STEPS = 2

// The deepest a project map's lower bound may sit, whatever the box fits at. Without it the
// bound follows the map's upper end: `getBoundsZoom` clamps the fitted zoom to `getMaxZoom()`,
// so a project whose box still fits its container at that level fits exactly there, and raising
// the tile layer's range would quietly move the bound with it.
export const MAP_PROJECT_ZOOM_FLOOR_MAX = 16

// What `maxBounds` adds around the section a project map shows at its lower bound, as a share of
// that section per side. It is what is left to pan: Leaflet pins an axis whose view is wider than
// `maxBounds` to the middle, so bounds drawn around the box itself would hold the map still.
export const MAP_PROJECT_MAX_BOUNDS_PADDING = 0.25

// What the overview map's starting fit keeps between the projects and the edge, per side. The
// same distance the map's own controls keep from that edge. The group of controls floating over
// the top is added to it, because it takes pointer events over the whole width; the ones in the
// bottom corner are not, so a project whose outermost point lies there can be under a button.
export const MAP_FIT_PADDING = 16

export const MAP_LAT_OFFSET = 0.002

export const MAP_DEFAULT_ZOOM = 13

export const MAP_GEOJSON_PATH_OPTIONS = {
  color: 'var(--action)',
  fillColor: 'var(--action)',
  fillOpacity: 0.15,
  opacity: 1,
  weight: 3
}

export const MAP_GEOJSON_HOVER_PATH_OPTIONS = {
  color: 'var(--action-hover)',
  fillColor: 'var(--action-hover)',
  fillOpacity: 0.25,
  opacity: 1,
  weight: 3
}

// A feature that brings its own colours keeps them on hover and only gets a thicker line: an
// opaque polygon and a line without a fill would both look unchanged if the fill carried the
// signal.
export const MAP_GEOJSON_HOVER_WEIGHT_STEP = 2

// A point is drawn as a circle, the way the CMS map draws it: a ring in its own colour over a
// light centre. The fill is Leaflet's own default, which is what the CMS ends up with.
export const MAP_POINT_RADIUS = 6
export const MAP_POINT_FILL_OPACITY = 0.2

// The stand-in for a project with nothing to draw, because every feature sits outside its zoom
// range or carries an image instead of a shape. Filled, because a circle this size has no area to
// carry a tint, and the ring is what separates the fill from the map underneath. It stays filled
// while the points around it are rings; that is deliberate, it stands for a project rather than
// for a place.
export const MAP_STAND_IN_POINT_OPTIONS = {
  color: 'var(--on-action)',
  fillColor: 'var(--action)',
  fillOpacity: 1,
  opacity: 1,
  radius: 7,
  weight: 2
}

// Where a feature's picture lies against the shapes it shares a pane with. Every path of a map is
// drawn into one `<svg>` of the renderer, while an image overlay is its own `<img>` beside it, so
// the two are separated by z-index and not by their place in the document: without a value of its
// own the picture lies under every shape, whichever was added first. Which value is enough depends
// on the stylesheet, and the two in play disagree by two orders of magnitude: `mapbox.css`, which
// the maps import, gives that `<svg>` 2, and `leaflet.css` gives it 200. This sits above both, so
// swapping the stylesheet cannot quietly push the pictures back under the shapes. It stays inside
// the pane, which is a stacking context of its own, and so never reaches over the markers or the
// stand-in point.
export const MAP_OVERLAY_IMAGE_Z_INDEX = 300

// The invisible layer that catches clicks. A line needs a wide stroke to be catchable; a circle
// gets the same stroke and makes up the rest with its radius, so both end at the same target.
// Measured that way rather than switched off for points, because the style function only ever
// sees the whole feature — a point inside a GeometryCollection would keep the line and grow.
export const MAP_HIT_AREA_WEIGHT = 20
const MAP_HIT_AREA_TARGET = 44
export const MAP_POINT_HIT_AREA_RADIUS = (MAP_HIT_AREA_TARGET - MAP_HIT_AREA_WEIGHT) / 2
